import Link from "next/link"
import classNames from "classnames"
import { getTeamPageUrl } from "../../../../utils/urls"
import { LocalizedGroupTeamStanding } from "../../../../types/groupTeamStanding"
import * as ImageConstants from "../../../../constants/images"
import { LocalizedMatch } from "types/match"
import { useMemo } from "react"
import { LocalizedCompetition } from "types/competition"
import { LocalizedTeam } from "types/team"
import style from "./style.module.scss"
import { Row, Col } from "react-bootstrap"
import cx from "classnames"

type Stats = {
  total: number
  win: number
  lost: number
  draw: number
  goal1: number
  goal2: number
}

export function MatchTeamComparisonStats({
  matches: allMatches,
  homeTeam,
  awayTeam,
}: {
  matches: LocalizedMatch[]
  homeTeam: LocalizedTeam
  awayTeam: LocalizedTeam
}) {
  const homeTeamId = homeTeam.id
  const awayTeamId = awayTeam.id

  const [stats, competitions] = useMemo(() => {
    const stats: {
      [competitionId: string]: {
        home: Stats
        away: Stats
        all: Stats
      }
    } = {}
    const competitions: LocalizedCompetition[] = []

    allMatches.forEach((match) => {
      if (!stats[match.competitionId]) {
        competitions.push(match.competition!)
      }
      stats[match.competitionId] = stats[match.competitionId] || {
        home: {
          total: 0,
          win: 0,
          lost: 0,
          draw: 0,
          goal1: 0,
          goal2: 0,
        },
        away: {
          total: 0,
          win: 0,
          lost: 0,
          draw: 0,
          goal1: 0,
          goal2: 0,
        },
        all: {
          total: 0,
          win: 0,
          lost: 0,
          draw: 0,
          goal1: 0,
          goal2: 0,
        },
      }
      let goal1, goal2, isHome
      if (match.homeTeamId === homeTeamId) {
        goal1 = match.homeScore
        goal2 = match.awayScore
        isHome = true
      } else {
        goal1 = match.awayScore
        goal2 = match.homeScore
        isHome = false
      }

      if (typeof goal1 !== "number" || typeof goal2 !== "number") {
        return
      }

      const all = stats[match.competitionId].all
      const homeOrAway = isHome ? stats[match.competitionId].home : stats[match.competitionId].away

      all.total++
      homeOrAway.total++

      if (goal1 > goal2) {
        all.win++
        homeOrAway.win++
      } else if (goal1 < goal2) {
        all.lost++
        homeOrAway.lost++
      } else {
        all.draw++
        homeOrAway.draw++
      }

      all.goal1 += goal1
      all.goal2 += goal2
      homeOrAway.goal1 += goal1
      homeOrAway.goal2 += goal2
    })
    return [stats, competitions]
  }, [allMatches, homeTeamId, awayTeamId])

  return (
    <div className={style.matchTeamComparisonStats}>
      <Row className={style.tableContainer}>
        {competitions.map((competition) => {
          const competitionStats = stats[competition.id]

          return (
            <table data-sort="points" key={competition.id}>
              <thead>
                <tr>
                  <th>{competition.name}</th>
                  <th>PG</th>
                  <th>V</th>
                  <th>P</th>
                  <th>S</th>
                  <th>Gol</th>
                  <th>Diff</th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    key: "home",
                    label: "In casa",
                  },
                  {
                    key: "away",
                    label: "Fuori casa",
                  },
                  {
                    key: "all",
                    label: "Totale",
                  },
                ].map(({ key, label }, index) => {
                  const isOdd = (index + 1) % 2 === 1
                  const thisStats = (competitionStats as any)[key] as Stats

                  return (
                    <tr
                      key={index}
                      className={cx("standing standing-" + (index + 1), index === 0 && "first", isOdd ? "odd" : "even")}
                    >
                      <td>{label}</td>
                      <td>{thisStats.total}</td>
                      <td>{thisStats.win}</td>
                      <td>{thisStats.draw}</td>
                      <td>{thisStats.lost}</td>
                      <td>{`${thisStats.goal1}:${thisStats.goal2}`}</td>
                      <td>{thisStats.goal1 - thisStats.goal2}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )
        })}
      </Row>
    </div>
  )
}
